import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { BaseButton } from 'components/BaseButton';
import { Party } from '../types';
import { buildRecipientOptions, matchesRecipient, RecipientOption } from '../utils/recipients';

interface RecipientsPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (values: string[]) => void;
  parties: Party[];
  targetLabel: string;
  t: TFunction;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      padding: theme.spacing(0, 1, 0, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1) },
    },
    target: {
      display: 'block',
      color: theme.palette.secondary.main,
    },
    searchBox: {
      flexShrink: 0,
      padding: theme.spacing(1, 2, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1, 3, 0) },
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(1, 2, 2),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1, 3, 2) },
    },
    group: {
      marginBottom: theme.spacing(1.5),
    },
    groupHeader: {
      color: theme.palette.primary.main,
      margin: theme.spacing(1, 0, 0.5),
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.5),
      borderRadius: 4,
      cursor: 'pointer',
      '&:hover': { backgroundColor: theme.palette.table.hover },
    },
    rowText: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    rowName: {
      color: theme.palette.primary.dark,
      fontWeight: 500,
      overflowWrap: 'anywhere',
    },
    address: {
      color: theme.palette.text.light,
      overflowWrap: 'anywhere',
    },
    emptyState: {
      padding: theme.spacing(4, 2),
      textAlign: 'center',
    },
    emptyText: {
      color: theme.palette.text.light,
    },
    actions: {
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      flexShrink: 0,
      padding: theme.spacing(1, 2, 2),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
    },
  })
);

const RecipientsPicker: FC<RecipientsPickerProps> = ({
  open,
  onClose,
  onConfirm,
  parties,
  targetLabel,
  t,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  if (!open && (search !== '' || selected.length > 0)) {
    setSearch('');
    setSelected([]);
  }

  const options = useMemo(() => buildRecipientOptions(parties), [parties]);

  const filtered = useMemo(
    () => options.filter((option) => matchesRecipient(option, search)),
    [options, search]
  );

  const groups = useMemo(() => {
    const byRole = new Map<string, RecipientOption[]>();
    filtered.forEach((option) => {
      const role = option.roles[0] ?? '';
      const list = byRole.get(role) ?? [];
      list.push(option);
      byRole.set(role, list);
    });
    return Array.from(byRole.entries())
      .sort(([a], [b]) => {
        if (a === b) return 0;
        if (a === '') return 1;
        if (b === '') return -1;
        return a.localeCompare(b);
      })
      .map(([role, items]) => ({ role, items }));
  }, [filtered]);

  const toggle = (value: string) => {
    setSelected((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
    );
  };

  const handleAdd = () => {
    if (selected.length === 0) return;
    onConfirm(selected);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <Box className={classes.header}>
        <DialogTitle>
          <Typography color="primary" variant="h5" component="h2">{t('messages:select-recipients')}</Typography>
          <Typography variant="caption" className={classes.target}>{targetLabel}</Typography>
        </DialogTitle>
        <IconButton aria-label={t('common:close')} onClick={onClose} size="large">
          <FontAwesomeIcon icon="times" />
        </IconButton>
      </Box>

      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common:search')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon="search" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className={classes.list}>
        {groups.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2" className={classes.emptyText}>{t('messages:no-matching-contacts')}</Typography>
          </Box>
        ) : groups.map(({ role, items }) => (
          <Box key={role || 'no-role'} className={classes.group}>
            {role && <Typography variant="subtitle2" className={classes.groupHeader}>{role}</Typography>}
            {items.map((option) => (
              <Box key={option.id} className={classes.row} onClick={() => toggle(option.value)}>
                <Checkbox
                  color="secondary"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                  onClick={(event) => event.stopPropagation()}
                />
                <Box className={classes.rowText}>
                  <Typography variant="body2" className={classes.rowName}>{option.name}</Typography>
                  <Typography variant="caption" className={classes.address}>
                    {option.address}
                    {option.emailKind ? ` · ${option.emailKind}` : ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <DialogActions className={classes.actions}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="times"
          text={t('common:cancel')}
          onClick={onClose}
        />
        <BaseButton
          buttonVariant="contained"
          buttonColor="secondary"
          icon="check"
          text={t('common:add')}
          disabled={selected.length === 0}
          onClick={handleAdd}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RecipientsPicker;
